import {
  Card,
  Checkbox,
  CrudMode,
  DataDisplay,
  Grid,
  GridBreak,
  Stack,
  ValidationErrors,
  formatBoolean,
  formatDate,
  formatPhoneNumber,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { AddressFields, DatePicker, Input, Select } from "components";
import { Address, Provider, ProviderEditResource, ProviderViewResource } from "generated/openApi";

import produce from "immer";

export const ProviderBasicInfo = (props: {
  provider: Provider;
  setProvider: (value: Provider) => void;
  viewResource: ProviderViewResource;
  editResource: ProviderEditResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const addressProperties = properties.find((x) => x.propertyName === pn<Provider>("address"))?.children || [];
  const addressValidationErrors = props.validationErrors[pn<Provider>("address")] || [];

  const lastNameField = () => {
    const propertyName = pn<Provider>("lastName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.provider.lastName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.provider.lastName}
        onChange={(e) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.lastName = e.target.value;
            })
          )
        }
      />
    );
  };

  const firstNameField = () => {
    const propertyName = pn<Provider>("firstName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.provider.firstName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.provider.firstName}
        onChange={(e) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.firstName = e.target.value;
            })
          )
        }
      />
    );
  };

  const enrollmentDateField = () => {
    const propertyName = pn<Provider>("enrollmentDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.provider.enrollmentDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.provider.enrollmentDate}
        onChange={(value) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.enrollmentDate = value;
            })
          )
        }
      />
    );
  };

  const positionField = () => {
    const propertyName = pn<Provider>("positionId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.provider.position?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.positions?.find((x) => x.id === props.provider.positionId)}
        options={props.editResource.positions}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.positionId = value?.id;
            })
          )
        }
      />
    );
  };

  const homePhoneField = () => {
    const propertyName = pn<Provider>("homePhone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label="Phone">{formatPhoneNumber(props.provider.homePhone)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.provider.homePhone}
        onChange={(e) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.homePhone = e.target.value;
            })
          )
        }
      />
    );
  };

  const cellPhoneField = () => {
    const propertyName = pn<Provider>("cellPhone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label="Phone">{formatPhoneNumber(props.provider.cellPhone)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.provider.cellPhone}
        onChange={(e) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.cellPhone = e.target.value;
            })
          )
        }
      />
    );
  };

  const emailField = () => {
    const propertyName = pn<Provider>("email");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.provider.email}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.provider.email}
        onChange={(e) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.email = e.target.value;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<Provider>("school");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.provider.school}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.provider.school}
        onChange={(e) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.school = e.target.value;
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<Provider>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.provider.note}
      </DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        multiline
        value={props.provider.note}
        onChange={(e) =>
          props.setProvider(
            produce(props.provider, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Card header={"Info"}>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {lastNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {firstNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {enrollmentDateField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {positionField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {homePhoneField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {cellPhoneField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {emailField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {schoolField()}
        </Grid>

        <GridBreak />

        <Grid item xs={fullFieldBreakpoints}>
          <AddressFields
            crudMode={props.crudMode}
            properties={addressProperties}
            address={props.provider.address || ({} as Address)}
            setAddress={(address) =>
              props.setProvider(
                produce(props.provider, (draft) => {
                  draft.address = address;
                })
              )
            }
            validationErrors={addressValidationErrors}
          />
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          {noteField()}
        </Grid>
      </Grid>
    </Card>
  );
};
